import { Drawer } from 'antd'
import styled from 'styled-components'
import { Text, Button, Heading } from '@lmc/ui-kit/src/general'
import { Spinner, Edit } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import StockCount from '../../iconsV2/StockCount'
import Delivery from '../../iconsV2/Delivery'
import Disposal from '../../iconsV2/Disposal'
import Administration from '../../iconsV2/Administration'
import { Tag } from 'antd/lib'
import { StockCheckDetailContent } from './StockCheckDetailContent'
import { AdministrationDetailContent } from './AdministrationDetailContent'
import { DeliveryDetailContent } from './DeliveryDetailContent'
import { DisposalDetailContent } from './DisposalDetailContent'
import { AdditionalDetailsSection } from './AdditionalDetailsSection'
import { MedicationStockReason } from '../../types'
import { getActionTitle } from '../../utils/stockRecordUtils'

type StockRecordDrawerProps = {
  isVisible: boolean
  onClose: () => void
  record: any
  medicationName: string
  onEdit?: (record: any, actionType: MedicationStockReason) => void
}

const renderActionIcon = (type: MedicationStockReason) => {
  switch (type) {
    case MedicationStockReason.STOCK_COUNT:
      return <StockCount />
    case MedicationStockReason.WASTE:
      return <Disposal />
    case MedicationStockReason.RESTOCK:
      return <Delivery />
    case MedicationStockReason.MED_ADMIN:
      return <Administration />
    default:
      return null
  }
}

export const StockRecordDrawer = ({
  isVisible,
  onClose,
  record,
  medicationName,
  onEdit
}: StockRecordDrawerProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const isLoading = false

  const actionType = record?.actionType as MedicationStockReason | undefined

  const handleEdit = () => {
    if (onEdit && actionType && record) {
      onEdit(record, actionType)
      onClose()
    }
  }

  if (!actionType || !record) return null

  const titleText = getActionTitle(actionType, t)
  const icon = renderActionIcon(actionType)

  const showEditButton = actionType !== MedicationStockReason.MED_ADMIN

  return (
    <StyledDrawer
      title={
        <>
          {isLoading && (
            <LoadingWrapper>
              <Spinner />
            </LoadingWrapper>
          )}
          {!isLoading && record && (
            <TitleWrapper>
              <MedicationNameRow>
                <MedicationNameText>{medicationName}</MedicationNameText>
                <Tag>CD</Tag>
              </MedicationNameRow>
              <TitleContainer>
                {icon && <IconWrapper>{icon}</IconWrapper>}
                <StyledTitleHeading level={4}>{titleText}</StyledTitleHeading>
              </TitleContainer>
            </TitleWrapper>
          )}
        </>
      }
      placement="right"
      onClose={onClose}
      open={isVisible}
      width={736}
    >
      {isLoading && (
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      )}
      {!isLoading && record && (
        <DrawerContent>
          <TopContent>
            <DetailsHeading level={3}>Details</DetailsHeading>
            {showEditButton && (
              <Button variant="text" onClick={handleEdit}>
                <Edit />
                {t('EDIT')}
              </Button>
            )}
          </TopContent>
          {actionType === MedicationStockReason.STOCK_COUNT && (
            <StockCheckDetailContent
              record={record}
              medicationName={medicationName}
            />
          )}
          {actionType === MedicationStockReason.MED_ADMIN && (
            <AdministrationDetailContent
              record={record}
              medicationName={medicationName}
            />
          )}
          {actionType === MedicationStockReason.RESTOCK && (
            <DeliveryDetailContent
              record={record}
              medicationName={medicationName}
            />
          )}
          {actionType === MedicationStockReason.WASTE && (
            <DisposalDetailContent
              record={record}
              medicationName={medicationName}
            />
          )}
          {actionType !== MedicationStockReason.STOCK_COUNT &&
            actionType !== MedicationStockReason.MED_ADMIN &&
            actionType !== MedicationStockReason.RESTOCK &&
            actionType !== MedicationStockReason.WASTE && (
              <div>{t('DETAILS_COMING_SOON')}</div>
            )}
          <AdditionalDetailsSection record={record} />
        </DrawerContent>
      )}
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  }

  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`

const StyledTitleHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`

const DrawerContent = styled.div``

const MedicationNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MedicationNameText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const DetailsHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const TopContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 0 16px 0;
`
